import InfiniteScroll from "react-infinite-scroll-component"
import { useItemCatalogQuery } from "./apiQueries"
import { Button, Flex, Skeleton, Space, Tag, Typography } from "antd"
import { CatalogItem } from "../../domain/catalogItem"
import { centsToDollarsDisplay } from "../displayFormatters"
import { ItemClasses, itemClassName } from '../../common/constants'
import Grip from "./Grip"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlus } from "@fortawesome/pro-solid-svg-icons"
import VSpace from "../common/VSpace"

const { Text } = Typography

type Props = {
  search: string
  draggable: boolean
  selectedClassIds: number[]
  selectedCategories: string[]
  onAdd: (item: CatalogItem) => void
  filterOpen: boolean
  fixedHeight?: string
  docked?: boolean
}

export const ItemList = ({ search, draggable, selectedClassIds, selectedCategories, onAdd, filterOpen, fixedHeight, docked }: Props) => {
  const pageSize = 50
  const itemsQuery = useItemCatalogQuery(search, pageSize, selectedClassIds, selectedCategories)

  const itemCatalog = itemsQuery.data?.pages.flatMap(x => x) ?? []
  const hasMore = !itemsQuery.data?.pages.some(x => x.length < pageSize)

  const height = fixedHeight ? fixedHeight : '100vh'
  const yOffset = (filterOpen ? 445 : 270) + (docked && filterOpen ? 100 : 0 )

  return <InfiniteScroll
    height={`calc(${height} - ${yOffset}px)`}
    dataLength={itemCatalog.length}
    hasMore={hasMore}
    next={() => itemsQuery.fetchNextPage()}
    loader={<Skeleton active paragraph={{ rows: 1 }} />}
    style={{
      transition: 'height 250ms ease-in-out'
    }}
  >
    {
      itemCatalog.map(item => (
        <CardLayout
          key={item.guid}
          item={item}
          onAdd={onAdd}
          draggable={draggable}
        />
      ))
    }
  </InfiniteScroll>
}

export default ItemList

type CardLayoutProps = {
  draggable?: boolean
  item: CatalogItem
  onAdd: (item: CatalogItem) => void
}

const CardLayout = ({ item, onAdd, draggable }: CardLayoutProps) => {
  const classTagColors: Record<number, string> = {
    [ItemClasses.Labor]: "blue",
    [ItemClasses.Equipment]: "orange",
    [ItemClasses.Materials]: "green",
    [ItemClasses.Subcontracting]: "purple",
  }

  const getClassTagColor = (itemClass: number) => {
    return classTagColors[itemClass] || "default"
  }

  return (
    <Flex justify="space-between" align="center" style={{ backgroundColor: 'white', padding: '8px 12px', border: '1px solid #e8e8e8' }}>
      <Space>
        {draggable && <Grip catalogItem={item} />}
        <VSpace>
          <Text>{item.name}</Text>
          <Text type="secondary">{centsToDollarsDisplay(item.adjustedCost)}{item.units && <>/{item.units}</>}</Text>
          <Tag color={getClassTagColor(item.class)}>{itemClassName(item.class)}</Tag>
        </VSpace>
      </Space>
      <Flex>
        <Button shape="circle" icon={<FontAwesomeIcon icon={faPlus} />} onClick={() => onAdd(item)} size="small" />
      </Flex>
    </Flex>
  )
}